import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../api/client';
import Modal from '../components/Modal';
import PassCard from '../components/PassCard';
import { useAuth } from '../contexts/AuthContext';
import { Plus, IdCard, Power, RefreshCw, Share2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { format, addMonths, parseISO } from 'date-fns';

export default function MonthlyPasses() {
  const { user } = useAuth();
  const [passes, setPasses] = useState([]);
  const [filter, setFilter] = useState('all');
  const [openCreate, setOpenCreate] = useState(false);
  const [view, setView] = useState(null);
  const canManage = ['admin', 'hr'].includes(user.role);

  const load = async () => {
    const params = {};
    // Non-managing roles only ever see their own pass. The backend auto-scopes
    // employees, but supervisors would get every pass without this.
    if (!canManage) params.mine = 'true';
    if (filter === 'active') params.active = true;
    if (filter === 'inactive') params.active = false;
    const { data } = await api.get('/monthly-passes', { params });
    setPasses(data);
  };

  useEffect(() => { load(); }, [filter]);

  const toggle = async (p) => {
    const reason = !p.active ? null : prompt('Reason for deactivation (optional)', '');
    try {
      await api.post(`/monthly-passes/${p.id}/activate`, { active: !p.active, reason });
      toast.success(p.active ? 'Deactivated' : 'Activated');
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, e?.response?.data));
    }
  };

  const [renewing, setRenewing] = useState(null);
  const renew = (p) => setRenewing(p);

  return (
    <div className="space-y-6" data-testid="monthly-passes-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          {canManage ? (
            <>
              <div className="overline">Module 05 · HR</div>
              <h1 className="heading text-3xl font-black text-ink">Monthly Employee Passes</h1>
              <p className="text-sm text-zinc-500 mt-1">Printable QR-based passes · Activate / Deactivate / Renew</p>
            </>
          ) : (
            <>
              <div className="overline">Module 05 · My Pass</div>
              <h1 className="heading text-3xl font-black text-ink">My Monthly Pass</h1>
              <p className="text-sm text-zinc-500 mt-1">Your QR gate pass · Show it at the gate for entry and exit</p>
            </>
          )}
        </div>
        {canManage && (
          <button className="btn-primary" onClick={() => setOpenCreate(true)} data-testid="new-monthly-btn"><Plus size={16} /> Issue New Pass</button>
        )}
      </header>

      <div className="flex gap-1 flex-wrap">
        {['all', 'active', 'inactive'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`monthly-filter-${f}`}
            className={`text-xs font-bold uppercase px-3 py-2 rounded-sm border ${filter === f ? 'bg-ink text-white border-ink' : 'bg-white border-zinc-300'}`}
          >{f}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {passes.length === 0 && <div className="card p-12 text-center text-zinc-400 col-span-full">No monthly passes.</div>}
        {passes.map((p) => (
          <div key={p.id} className={`card p-4 ${!p.active ? 'opacity-60' : ''}`} data-testid={`monthly-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-bold text-ink">{p.employee_name}</div>
                <div className="text-xs text-zinc-500 font-mono">{p.employee_code || '—'}</div>
              </div>
              <span className={p.active ? 'badge-success' : 'badge-danger'}>{p.active ? 'Active' : 'Inactive'}</span>
            </div>
            <div className="text-xs text-zinc-600 space-y-1">
              <div>Dept: <span className="font-semibold text-ink">{p.department || '—'}</span></div>
              <div>Validity: {p.valid_from} → {p.valid_until}</div>
              <div className="font-mono font-bold">{p.pass_id}</div>
            </div>
            {p.deactivated_reason && (
              <div className="text-xs text-red-700 mt-2 bg-red-50 border border-red-200 p-2 rounded-sm">{p.deactivated_reason}</div>
            )}
            <div className="mt-3 pt-3 border-t border-zinc-100 flex gap-2 flex-wrap">
              <button className="btn-outline text-xs" onClick={() => setView(p)} data-testid={`view-monthly-${p.id}`}><IdCard size={12} /> View</button>
              {canManage && (
                <>
                  <button className="btn-outline text-xs" onClick={() => toggle(p)} data-testid={`toggle-monthly-${p.id}`}>
                    <Power size={12} /> {p.active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button className="btn-outline text-xs" onClick={() => renew(p)} data-testid={`renew-monthly-${p.id}`}><RefreshCw size={12} /> Renew</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {openCreate && <IssueModal onClose={() => setOpenCreate(false)} onCreated={() => { setOpenCreate(false); load(); }} />}
      {view && <ViewModal pass={view} onClose={() => setView(null)} />}
      {renewing && (
        <RenewModal
          pass={renewing}
          onClose={() => setRenewing(null)}
          onRenewed={() => { setRenewing(null); load(); }}
        />
      )}
    </div>
  );
}

function IssueModal({ onClose, onCreated }) {
  const [employees, setEmployees] = useState([]);
  // employee_id → their active pass. One active pass per person: these people are shown but
  // can't be picked — HR renews their existing pass instead (the backend enforces the same rule).
  const [activeByEmployee, setActiveByEmployee] = useState({});
  const [form, setForm] = useState({
    employee_id: '',
    valid_from: format(new Date(), 'yyyy-MM-dd'),
    valid_until: format(addMonths(new Date(), 1), 'yyyy-MM-dd'),
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Any active user can hold a pass, whatever their role.
    api.get('/users').then(({ data }) => setEmployees(data.filter((u) => u.active)));
    api.get('/monthly-passes', { params: { active: true } }).then(({ data }) => {
      const map = {};
      data.forEach((p) => { map[p.employee_id] = p; });
      setActiveByEmployee(map);
    });
  }, []);

  const today = todayStr();
  const passNote = (p) => (p.valid_until < today
    ? `pass expired ${fmtShort(p.valid_until)}, renew instead`
    : `has pass until ${fmtShort(p.valid_until)}, renew instead`);

  const submit = async (e) => {
    e.preventDefault();
    if (form.valid_until < form.valid_from) { setErr('Valid Until cannot be before Valid From.'); return; }
    setErr(''); setLoading(true);
    try {
      await api.post('/monthly-passes', form);
      toast.success('Pass issued');
      onCreated();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.response?.data));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Issue Monthly Pass" subtitle="HR · New Pass" size="md"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button form="mp-form" type="submit" className="btn-primary" disabled={loading} data-testid="submit-monthly">Issue Pass</button>
      </>}
    >
      <form id="mp-form" onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Employee *</label>
          <select required className="input" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="mp-employee">
            <option value="">Select employee</option>
            {employees.map((u) => {
              const existing = activeByEmployee[u.id];
              return (
                <option key={u.id} value={u.id} disabled={!!existing}>
                  {u.name} · {u.employee_code || u.email}{existing ? ` (${passNote(existing)})` : ''}
                </option>
              );
            })}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="label">Valid From</label><input type="date" className="input" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
          <div><label className="label">Valid Until</label><input type="date" className="input" min={form.valid_from} value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
        </div>
        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </form>
    </Modal>
  );
}

// Dates are ISO "YYYY-MM-DD" strings throughout (that's what the backend stores and the
// gate compares), so plain string comparison orders them correctly.
const todayStr = () => format(new Date(), 'yyyy-MM-dd');
// '2026-11-15' → '15 Nov'
const fmtShort = (ymd) => format(parseISO(ymd), 'd MMM');

function RenewModal({ pass, onClose, onRenewed }) {
  const today = todayStr();
  // A new expiry can't be in the past, or before the pass starts.
  const minDate = pass.valid_from > today ? pass.valid_from : today;
  // Default: one month after the later of today and the current expiry.
  const base = pass.valid_until > today ? pass.valid_until : today;
  const [date, setDate] = useState(format(addMonths(new Date(`${base}T00:00:00`), 1), 'yyyy-MM-dd'));
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const validate = (d) => {
    if (!d) return 'Select the new valid-until date.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || Number.isNaN(new Date(`${d}T00:00:00`).getTime())) return 'Enter a valid date.';
    if (d < today) return 'The new date cannot be in the past.';
    if (d < pass.valid_from) return `The new date cannot be before the pass start date (${pass.valid_from}).`;
    return '';
  };

  const submit = async (e) => {
    e.preventDefault();
    const problem = validate(date);
    if (problem) { setErr(problem); return; }
    setErr('');
    setLoading(true);
    try {
      await api.post(`/monthly-passes/${pass.id}/renew`, { valid_until: date });
      toast.success(`Pass renewed until ${format(new Date(`${date}T00:00:00`), 'd MMM yyyy')}`);
      onRenewed();
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail, e2?.response?.data));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Renew Monthly Pass" subtitle={pass.employee_name} size="sm"
      footer={<>
        <button className="btn-outline" onClick={onClose}>Cancel</button>
        <button form="renew-form" type="submit" className="btn-primary" disabled={loading} data-testid="confirm-renew">
          <RefreshCw size={14} /> {loading ? 'Renewing…' : 'Renew'}
        </button>
      </>}
    >
      <form id="renew-form" onSubmit={submit} className="space-y-4" noValidate>
        <div className="text-sm text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm p-3">
          <div className="overline mb-1">Current validity</div>
          {pass.valid_from} → {pass.valid_until}
          {pass.valid_until < today && <span className="ml-2 badge-danger">Expired</span>}
        </div>
        <div>
          <label className="label">New Valid Until *</label>
          <input
            type="date"
            className={`input ${err ? 'border-red-400' : ''}`}
            value={date}
            min={minDate}
            onChange={(e) => { setDate(e.target.value); setErr(''); }}
            data-testid="renew-date"
            autoFocus
          />
        </div>
        {!pass.active && (
          <div className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 p-2 rounded-sm">
            This pass is inactive. Renewing it will also reactivate it.
          </div>
        )}
        {err && <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-2 rounded-sm">{err}</div>}
      </form>
    </Modal>
  );
}

function ViewModal({ pass, onClose }) {
  const shareUrl = `${window.location.origin}/monthly-passes?pass=${pass.pass_id}`;
  const waText = encodeURIComponent(
    `*Monthly Gate Pass*\n\nName: ${pass.employee_name}\nCode: ${pass.employee_code || '—'}\nDept: ${pass.department || '—'}\nPass ID: ${pass.pass_id}\nPasscode: ${pass.passcode}\nValidity: ${pass.valid_from} → ${pass.valid_until}\n\n(Mocked WhatsApp share — integrate Twilio for real delivery)`
  );
  const shareWA = `https://wa.me/?text=${waText}`;

  return (
    <Modal open onClose={onClose} title={`Monthly Pass · ${pass.employee_name}`} subtitle="Printable · WhatsApp-ready" size="md"
      footer={<>
        <a className="btn-outline" href={shareWA} target="_blank" rel="noreferrer" data-testid="share-whatsapp"><Share2 size={14} /> Send via WhatsApp</a>
        <button className="btn-secondary" onClick={() => window.print()}><Printer size={14} /> Print</button>
      </>}
    >
      <div className="mb-4 text-xs p-3 bg-yellow-50 border border-yellow-200 rounded-sm text-yellow-800">
        <strong>MOCKED WHATSAPP:</strong> The button opens WhatsApp web with pre-filled message. Integrate Twilio for automated delivery in production.
      </div>
      <PassCard
        passId={pass.pass_id}
        passcode={pass.passcode}
        title={pass.employee_name}
        subtitle="MONTHLY EMPLOYEE PASS"
        variant="dark"
        lines={[
          { label: 'Code', value: pass.employee_code },
          { label: 'Department', value: pass.department },
          { label: 'Valid From', value: pass.valid_from },
          { label: 'Valid Until', value: pass.valid_until },
          { label: 'Issued By', value: pass.issued_by_name },
        ]}
      />
    </Modal>
  );
}
